import { useMemo } from 'react';
import { useQuote } from '../context/QuoteContext';
import type { QuoteStep, QuoteStepInfo } from '../types/quote.types';
import { STEP_TITLE } from '../utils/stepGate';
import { toStepPosition } from '../utils/stepPosition';

/**
 * #882 owns the shape (one entry per step, mapped over the RESOLVED order);
 * #825 owns the `title` values, which now come from `stepGate.ts`'s
 * `STEP_TITLE` rather than being restated here.
 *
 * Both sides of the merge had independently consolidated step copy — #882 into
 * this `STEP_LABELS` record, #825 into `STEP_TITLE` — and keeping both would
 * have left two literals for the same title. They cannot be allowed to drift:
 * `completionBlockedMessage` names the destination step by title ("Go back to
 * the Dealer step"), so a title that disagrees with the stepper sends the user
 * looking for a step that appears under a different name.
 *
 * `description` stays local. It is the stepper's alone — no gate message
 * composes it — so lifting it into the pure gate module would put copy there
 * that module has no use for.
 */
const STEP_DESCRIPTION: Record<QuoteStep, string> = {
    dealer: 'Choose the dealer for this service plan',
    vehicle: 'Find and select the customer vehicle',
    product: 'Choose the service plan product',
    configuration: 'Set up services, optional extras, and payment options',
    customer: 'Choose or create the customer',
    payment: 'Choose or create the payment options',
    summary: 'Review and complete the quote',
};

export const useQuoteSteps = () => {
    // stepOrder comes from useQuote() (Epic 7.1.8) — not a second
    // useQuoteStepOrder() call, to avoid two resolutions of one flag diverging.
    const quote = useQuote();

    const steps: QuoteStepInfo[] = useMemo(
        () =>
            quote.stepOrder.map((key) => ({
                key,
                title: STEP_TITLE[key],
                description: STEP_DESCRIPTION[key],
                isActive: quote.currentStep === key,
            })),
        [quote]
    );

    const currentStepIndex = steps.findIndex((step) => step.isActive);

    return {
        steps,
        currentStepIndex,
        totalSteps: steps.length,
        stepPosition: toStepPosition(currentStepIndex, steps.length),
    };
};
